import React, { useState, useEffect } from 'react';
import './App.css';
import emailjs from '@emailjs/browser';

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Spline 브랜딩 버튼 숨기기
  useEffect(() => {
    const hideSplineBranding = () => {
      // Shadow DOM 내부 요소 찾기
      const splineViewer = document.querySelector('spline-viewer');
      if (splineViewer && splineViewer.shadowRoot) {
        const links = splineViewer.shadowRoot.querySelectorAll('a');
        const buttons = splineViewer.shadowRoot.querySelectorAll('button');
        
        links.forEach(link => {
          if (link.href && (link.href.includes('spline') || link.textContent.includes('Built with'))) {
            link.style.display = 'none';
            link.style.visibility = 'hidden';
            link.style.opacity = '0';
            link.style.pointerEvents = 'none';
          }
        });
        
        buttons.forEach(button => {
          if (button.textContent && button.textContent.includes('Spline')) {
            button.style.display = 'none';
            button.style.visibility = 'hidden';
            button.style.opacity = '0';
            button.style.pointerEvents = 'none';
          }
        });
      }

      // 일반 DOM에서도 숨기기
      const allLinks = document.querySelectorAll('a[href*="spline"], a[href*="Built with"]');
      allLinks.forEach(link => {
        link.style.display = 'none';
        link.style.visibility = 'hidden';
        link.style.opacity = '0';
        link.style.pointerEvents = 'none';
      });
    };
 
    // 초기 실행
    hideSplineBranding();

    // MutationObserver로 동적 추가되는 요소 감지
    const observer = new MutationObserver(hideSplineBranding);
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // 주기적으로 체크 (Spline이 늦게 로드될 수 있음)
    const interval = setInterval(hideSplineBranding, 500);
    
    // 5초 후 interval 정리
    setTimeout(() => {
      clearInterval(interval);
    }, 5000);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 이메일 형식 검증
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 전화번호 형식 검증 (선택사항)
  const validatePhone = (phone) => {
    if (!phone.trim()) return true; // 전화번호는 선택사항
    const phoneRegex = /^[0-9-+\s()]+$/;
    return phoneRegex.test(phone);
  };

  // 폼 유효성 검증
  const validateForm = () => {
    if (!formData.name.trim()) {
      setSubmitStatus({ type: 'error', message: '이름을 입력해주세요.' });
      return false;
    }
    if (!formData.company.trim()) {
      setSubmitStatus({ type: 'error', message: '회사명을 입력해주세요.' });
      return false;
    }
    if (!formData.email.trim()) {
      setSubmitStatus({ type: 'error', message: '이메일을 입력해주세요.' });
      return false;
    }
    if (!validateEmail(formData.email)) {
      setSubmitStatus({ type: 'error', message: '올바른 이메일 형식을 입력해주세요.' });
      return false;
    }
    if (formData.phone.trim() && !validatePhone(formData.phone)) {
      setSubmitStatus({ type: 'error', message: '올바른 전화번호 형식을 입력해주세요.' });
      return false;
    }
    if (!formData.message.trim()) {
      setSubmitStatus({ type: 'error', message: '문의내용을 입력해주세요.' });
      return false;
    }
    if (formData.message.trim().length < 10) {
      setSubmitStatus({ type: 'error', message: '문의내용은 최소 10자 이상 입력해주세요.' });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // 폼 유효성 검증
    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    // EmailJS 설정 확인
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    // EmailJS 설정이 없는 경우 mailto 링크로 자동 대체
    if (!serviceId || !templateId || !publicKey || 
        serviceId === 'YOUR_SERVICE_ID' || 
        templateId === 'YOUR_TEMPLATE_ID' || 
        publicKey === 'YOUR_PUBLIC_KEY') {
      // mailto 링크로 대체 전송
      const subject = encodeURIComponent(`[팅에이아이] 문의사항 - ${formData.company}`);
      const phoneInfo = formData.phone.trim() ? `전화번호: ${formData.phone}\n` : '';
      const body = encodeURIComponent(
        `이름: ${formData.name}\n회사명: ${formData.company}\n이메일: ${formData.email}\n${phoneInfo}문의내용:\n${formData.message}`
      );
      window.location.href = `mailto:eric@tingai.co.kr?subject=${subject}&body=${body}`;
      setSubmitStatus({ 
        type: 'success', 
        message: '이메일 클라이언트가 열렸습니다. 이메일을 확인하고 전송해주세요.' 
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const templateParams = {
        user_name: formData.name.trim(),
        user_email: formData.email.trim(),
        company: formData.company.trim(),
        phone: formData.phone.trim() || '미입력',
        message: formData.message.trim(),
        reply_to: formData.email.trim()
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      setSubmitStatus({ 
        type: 'success', 
        message: '문의가 성공적으로 전송되었습니다. 빠른 시일 내에 답변드리겠습니다.' 
      });
      setFormData({ name: '', company: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('이메일 전송 실패:', error);
      
      // 에러 타입에 따른 상세 메시지
      let errorMessage = '문의 전송에 실패했습니다.';
      if (error.text) {
        errorMessage += ` (${error.text})`;
      } else if (error.message) {
        errorMessage += ` (${error.message})`;
      }
      errorMessage += ' 이메일(eric@tingai.co.kr)로 직접 문의해 주시기 바랍니다.';
      
      setSubmitStatus({ 
        type: 'error', 
        message: errorMessage
      });
      
      // 에러 발생 시에도 mailto 링크 제공 버튼 추가
      setTimeout(() => {
        const subject = encodeURIComponent(`[팅에이아이] 문의사항 - ${formData.company}`);
        const phoneInfo = formData.phone.trim() ? `전화번호: ${formData.phone}\n` : '';
        const body = encodeURIComponent(
          `이름: ${formData.name}\n회사명: ${formData.company}\n이메일: ${formData.email}\n${phoneInfo}문의내용:\n${formData.message}`
        );
        const useMailto = window.confirm('이메일 전송에 실패했습니다. 이메일 클라이언트로 직접 보내시겠습니까?');
        if (useMailto) {
          window.location.href = `mailto:eric@tingai.co.kr?subject=${subject}&body=${body}`;
        }
      }, 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="App">
      {/* Header */}
      <header className="header">
        <div className="header-container">
          <div className="logo">
            <img src="/tingAI_logo.png" alt="팅에이아이" className="logo-img" />
          </div>
          <nav className={`nav ${isMenuOpen ? 'nav-open' : ''}`}>
            <a href="#main">메인</a>
            <a href="#company">회사소개</a>
            <a href="#business">사업영역</a>
            <a href="#solutions">솔루션</a>
            <a href="#performance">수행실적</a>
            <a href="#rd">R&D</a>
            <a href="#contact">문의하기</a>
          </nav>
          <button className="menu-toggle" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section id="main" className="hero">
        <spline-viewer 
          url="https://prod.spline.design/HcLCqmwLotSa7HXN/scene.splinecode"
          className="spline-viewer"
        ></spline-viewer>
        <div className="hero-content">
          <h1 className="hero-title">Your Trusted Partner<br />for Industrial Digital Transformation</h1>
          <p className="hero-subtitle">
            금융·제조·건설 산업을 위한<br />
            정보시스템 개발·운영·유지보수 및<br />
            AI·데이터 기반 플랫폼 전문 기업
          </p>
        </div>
      </section>

      {/* Core Competencies */}
      <section className="section competencies-section">
        <div className="container">
          <h2 className="section-title">핵심 역량</h2>
          <p className="section-description">
            팅에이아이의 4가지 핵심 역량으로 고객의 디지털 전환을 지원합니다.
          </p>
          <div className="competencies-grid">
            <div className="competency-card">
              <div className="competency-icon">🏗️</div>
              <h3>ADM 전문성</h3>
              <p>건설사 중심 정보시스템 개발·운영·유지보수(ADM) 수행 전문성으로 안정적인 IT 인프라를 제공합니다.</p>
            </div>
            <div className="competency-card">
              <div className="competency-icon">🔗</div>
              <h3>통합 플랫폼</h3>
              <p>자체 솔루션 기반 업무·데이터·프로세스 통합 지원으로 업무 효율성을 극대화합니다.</p>
            </div>
            <div className="competency-card">
              <div className="competency-icon">🛡️</div>
              <h3>안정적 운영</h3>
              <p>복잡한 엔터프라이즈 환경에서의 안정적 운영 및 장애 최소화 체계를 구축합니다.</p>
            </div>
            <div className="competency-card">
              <div className="competency-icon">📊</div>
              <h3>데이터 분석</h3>
              <p>데이터 기반 성능 분석 및 시스템 고도화 역량으로 지속적인 개선을 제공합니다.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Business Areas */}
      <section id="business" className="section business-section">
        <div className="container">
          <h2 className="section-title">사업 영역</h2>
          <p className="section-description">
            건설·제조·금융 산업의 디지털 전환을 위한 전문 서비스를 제공합니다.
          </p>
          <div className="business-grid">
            <div className="business-card">
              <h3>IT 시스템 운영 및 유지보수(ADM)</h3>
              <p>대규모 엔터프라이즈 시스템의 안정적 운영과 체계적인 유지보수 서비스를 제공합니다.</p>
            </div>
            <div className="business-card">
              <h3>업무시스템 개발 및 고도화</h3>
              <p>고객의 비즈니스 요구사항에 맞춘 맞춤형 업무시스템 개발 및 지속적 고도화를 수행합니다.</p>
            </div>
            <div className="business-card">
              <h3>AI·데이터 기반 플랫폼</h3>
              <p>AI 및 빅데이터 기술을 활용한 분석 플랫폼 구축과 연구과제 수행을 지원합니다.</p>
            </div>
            <div className="business-card">
              <h3>전자결재 및 문서관리</h3>
              <p>SolForm을 통한 전자결재 플랫폼 구축으로 문서 관리 프로세스를 자동화합니다.</p>
            </div>
            <div className="business-card">
              <h3>데이터 처리 자동화</h3>
              <p>DONGRI를 활용한 ETL 및 데이터 마이그레이션 자동화로 효율성을 높입니다.</p>
            </div>
            <div className="business-card">
              <h3>업무 프로세스 관리</h3>
              <p>NEMO 플랫폼을 통한 업무·프로세스 통합 관리로 협업 효율을 향상시킵니다.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section id="solutions" className="section solutions-section">
        <div className="container">
          <h2 className="section-title">자체 솔루션</h2>
          <p className="section-description">
            고객의 비즈니스 요구사항을 충족하는 전문 솔루션을 제공합니다.
          </p>
          <div className="solutions-grid">
            <div className="solution-card">
              <div className="solution-header">
                <h3>SolForm</h3>
                <span className="solution-tag">전자결재 플랫폼</span>
              </div>
              <ul className="solution-features">
                <li>프로세스 자동화</li>
                <li>문서관리 기능 고도화</li>
                <li>사용자 편의성 개선</li>
              </ul>
              <p className="solution-description">
                전자결재와 문서관리를 통합한 플랫폼으로 업무 프로세스를 효율화합니다.
              </p>
            </div>
            <div className="solution-card">
              <div className="solution-header">
                <h3>DONGRI</h3>
                <span className="solution-tag">ETL 솔루션</span>
              </div>
              <ul className="solution-features">
                <li>데이터 처리 자동화 엔진</li>
                <li>대량 데이터 처리</li>
                <li>다중 파이프라인 안정성 강화</li>
              </ul>
              <p className="solution-description">
                ETL 프로세스를 자동화하고 데이터 마이그레이션을 효율적으로 수행합니다.
              </p>
            </div>
            <div className="solution-card">
              <div className="solution-header">
                <h3>NEMO</h3>
                <span className="solution-tag">업무·프로세스 관리</span>
              </div>
              <ul className="solution-features">
                <li>업무 흐름 관리</li>
                <li>조직 단위 협업 지원</li>
                <li>관리 효율화</li>
              </ul>
              <p className="solution-description">
                업무와 프로세스를 통합 관리하여 조직의 협업 효율을 극대화합니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Performance */}
      <section id="performance" className="section performance-section">
        <div className="container">
          <h2 className="section-title">수행 실적</h2>
          <p className="section-description">
            대기업 및 공공기관과의 신뢰를 바탕으로 축적한 노하우를 제공합니다.
          </p>
          <div className="performance-content">
            <div className="clients-section">
              <h3>주요 고객사</h3>
              <div className="clients-grid">
                <div className="client-card">
                  <img src="/hyundai_ erection.png" alt="현대건설" className="client-logo" />
                </div>
                <div className="client-card">
                  <img src="/hyundai_ engineering.png" alt="현대엔지니어링" className="client-logo" />
                </div>
                <div className="client-card">
                  <img src="/hyundai_still.png" alt="현대스틸" className="client-logo" />
                </div>
              </div>
            </div>
            <div className="projects-section">
              <h3>주요 수행 과제</h3>
              <ul className="projects-list">
                <li>IT 시스템 운영 및 유지보수(ADM)</li>
                <li>업무시스템 개발 및 고도화</li>
                <li>AI·데이터 기반 분석 및 연구과제 수행</li>
                <li>PM/현장대리인 기반 리스크·품질 관리 및 성과 보고 시스템</li>
                <li>공공 R&D 과제: K-Doctor 시스템 안정화 연구과제 수행</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section id="company" className="section company-section">
        <div className="container">
          <h2 className="section-title">회사 소개</h2>
          <div className="company-content">
            <div className="company-overview">
              <h3>회사 개요</h3>
              <p>
                팅에이아이는 건설·제조·금융 분야를 중심으로 정보시스템 개발, 운영, 유지보수(ADM)와 
                AI·데이터 기반 플랫폼 구축에 전문화된 기업입니다.
              </p>
              <p>
                현업 프로세스에 대한 높은 이해, 대규모 엔터프라이즈 환경의 안정적 운영 기술, 
                자체 솔루션을 기반으로 고객사의 디지털 업무 환경을 효율적으로 개선하는 것이 강점입니다.
              </p>
            </div>
            <div className="company-vision">
              <h3>비전 및 기술 경쟁력</h3>
              <p>
                팅에이아이는 "현업을 이해하는 IT, 안정적으로 운영되는 시스템"이라는 비전을 통해 
                고객의 성공적인 디지털 전환을 지원합니다.
              </p>
              <p>
                건설·제조·금융 산업에 대한 깊은 이해와 기술 전문성을 바탕으로 
                신뢰할 수 있는 IT 파트너로서 지속적인 성장을 추구합니다.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* R&D */}
      <section id="rd" className="section rd-section">
        <div className="container">
          <h2 className="section-title">R&D</h2>
          <p className="section-description">
            지속적인 연구개발을 통해 최신 기술을 도입하고 혁신적인 솔루션을 제공합니다.
          </p>
          <div className="rd-content">
            <div className="rd-card">
              <h3>공공 R&D 과제 수행</h3>
              <p>K-Doctor 시스템 안정화 연구과제를 수행하며 공공기관의 디지털 전환을 지원했습니다.</p>
            </div>
            <div className="rd-card">
              <h3>AI·데이터 연구</h3>
              <p>AI 및 빅데이터 기술 연구를 통해 고도화된 분석 플랫폼을 개발하고 있습니다.</p>
            </div>
            <div className="rd-card">
              <h3>솔루션 고도화</h3>
              <p>SolForm, DONGRI, NEMO 등 자체 솔루션의 지속적인 개선과 기능 강화를 추진합니다.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="section contact-section">
        <div className="container">
          <h2 className="section-title">문의하기</h2>
          <p className="section-description">
            팅에이아이와 함께 디지털 전환을 시작하세요. 전문가가 상담을 도와드립니다.
          </p>
          <div className="contact-content">
            <div className="contact-info">
              <h3>연락처 정보</h3>
              <p>기업 문의 및 제휴 상담을 원하시면 연락 주시기 바랍니다.</p>
              <div className="contact-details">
                <p><strong>이메일:</strong> eric@tingai.co.kr</p>
                {/* <p><strong>전화:</strong> 02-0000-0000</p> */}
              </div>
            </div>
            <div className="contact-form">
              <form onSubmit={handleSubmit}>
                {submitStatus && (
                  <div className={`submit-message ${submitStatus.type}`}>
                    {submitStatus.message}
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="name">이름</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    value={formData.name}
                    onChange={handleInputChange}
                    required 
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="company">회사명</label>
                  <input 
                    type="text" 
                    id="company" 
                    name="company" 
                    value={formData.company}
                    onChange={handleInputChange}
                    required 
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">이메일</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    value={formData.email}
                    onChange={handleInputChange}
                    required 
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">전화번호</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="phone" 
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="010-1234-5678 (선택사항)"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">문의내용</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    rows="5" 
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                </div>
                <button 
                  type="submit" 
                  className="btn-submit" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '전송 중...' : '문의하기'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="logo logo-white">
                <img src="/tingAI_logo.png" alt="팅에이아이" className="logo-img" />
              </div>
              <p className="footer-description">
                건설·제조·금융 산업을 위한 정보시스템 개발·운영·유지보수 및 AI·데이터 기반 플랫폼 전문 기업
              </p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>회사소개</h4>
                <a href="#company">회사개요</a>
                <a href="#company">비전 및 기술 경쟁력</a>
              </div>
              <div className="footer-column">
                <h4>사업영역</h4>
                <a href="#business">IT 시스템 운영·유지보수</a>
                <a href="#business">업무시스템 개발·고도화</a>
                <a href="#business">AI·데이터 기반 플랫폼</a>
              </div>
              <div className="footer-column">
                <h4>솔루션</h4>
                <a href="#solutions">SolForm</a>
                <a href="#solutions">DONGRI</a>
                <a href="#solutions">NEMO</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2023 TingAI. All Rights Reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
